// Is there ink on this pad, and what may be written over a stored signature?
//
// NEVER ASK A PAD isEmpty() — ASK THE PIXELS (2026-08-08, Jill's Anthem slip).
//
// Greg Styrk's signature vanished off a slip that had been signed, and what was
// left in its place was a 1x1 transparent PNG. signature_pad's own bookkeeping
// is the reason, and it lies in BOTH directions:
//
//   fromDataURL()  sets _isEmpty = false SYNCHRONOUSLY, then paints the ink in
//                  an image.onload later. In between, isEmpty() says "signed"
//                  while the canvas is blank — export it and you write a 1x1
//                  transparent PNG over the real signature, on the device and
//                  in the account.
//   clear()        sets _isEmpty = true. The old refit in SignatureField called
//                  clear() and replayed the ink through another async Image(),
//                  so after every container resize (rotation, keyboard,
//                  collapsing a pilot block) the pad reported EMPTY while ink
//                  was plainly on screen. An autosave landing in that window
//                  wrote null: signature gone.
//
// So nothing here trusts _isEmpty. Draft durability is paramount: no signature
// may be overwritten by a blank the pilot did not draw. The contract that
// enforces it is exportSignature's three-way answer — see below.

// Pads with a restore in flight. A WeakMap keeps the marker off the instance
// (and lets a discarded pad be collected). Counted, because the slip editor
// retries its restore and the attempts can overlap.
const RESTORING = new WeakMap();
const RESTORE_GIVE_UP_MS = 5000;

export function beginPadRestore(pad) {
  if (!pad) return;
  RESTORING.set(pad, (RESTORING.get(pad) || 0) + 1);
  // An image that never loads and never errors must not leave the pad frozen
  // as "restoring" for the rest of the session.
  setTimeout(() => endPadRestore(pad), RESTORE_GIVE_UP_MS);
}
export function endPadRestore(pad) {
  if (!pad) return;
  const left = (RESTORING.get(pad) || 0) - 1;
  if (left > 0) RESTORING.set(pad, left);
  else RESTORING.delete(pad);
}
export function isPadRestoring(pad) {
  return !!pad && (RESTORING.get(pad) || 0) > 0;
}

// getCanvas() THROWS while the component is mounting or unmounting.
function padCanvas(pad) {
  if (!pad || typeof pad.getCanvas !== "function") return null;
  try { return pad.getCanvas(); } catch { return null; }
}

// Does this canvas carry ink? True when any pixel differs from the top-left
// one — a blank pad is uniform (fully transparent, or a flat background fill),
// and a signature never is. Every pixel is checked, no sampling: skipping every
// other one is a millisecond faster and can miss a single stray mark, and the
// cost of a miss here is a pilot's signature. It also exits at the first
// difference, so an inked pad answers long before the end of the buffer.
export function canvasHasInk(canvas) {
  if (!canvas || !canvas.width || !canvas.height) return false;
  // trim-canvas returns 1x1 for a canvas with nothing on it.
  if (canvas.width <= 2 || canvas.height <= 2) return false;
  try {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return true; // can't look: assume ink, never destroy on a guess
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const r = data[0], g = data[1], b = data[2], a = data[3];
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] !== r || data[i + 1] !== g || data[i + 2] !== b || data[i + 3] !== a) return true;
    }
    return false;
  } catch {
    return true; // same reason: an unreadable canvas is not evidence of blank
  }
}

// Has the pilot signed? Pixel truth, plus an in-flight restore counts as signed
// (the ink is decoding, and a "sign here first" alert over a signed pad is a
// worse lie than waiting a beat).
export function padHasInk(pad) {
  if (!pad) return false;
  if (isPadRestoring(pad)) return true;
  return canvasHasInk(padCanvas(pad));
}

// The ONE way to get a signature out of a pad.
//   string     the signature, a PNG data URL
//   null       the pad is genuinely blank (never signed, or the pilot cleared it)
//   undefined  CANNOT TELL — no pad mounted, or a restore is still painting.
//              Callers MUST keep whatever they already had. Writing undefined
//              over a stored signature is the bug this whole module exists for.
// Signature pads render at on-screen size x devicePixelRatio (3x on iPhone),
// so a raw export is a ~1000px-wide PNG that weighs 100-250 KB as a data URL.
// Carried in every draft envelope, that weight is what filled Merrill's and
// John's storage quota (2026-08-05/06/12): one signed slip held locally plus
// its cloud mirror is ~400 KB of mostly signature pixels. 600px is ~200 dpi
// across the 2-3 inches a signature occupies on the printed slip — visibly
// identical on paper, ~5-10x lighter. Restores are unaffected:
// restoreSignatureFit scales DOWN to fit and never upscales, and a re-export
// of a restored signature trims to the same ink, so round-trips don't compound.
const EXPORT_MAX_PX = 600;

function downscaleCanvas(canvas) {
  if (canvas.width <= EXPORT_MAX_PX && canvas.height <= EXPORT_MAX_PX) return canvas;
  const scale = Math.min(EXPORT_MAX_PX / canvas.width, EXPORT_MAX_PX / canvas.height);
  const w = Math.max(1, Math.round(canvas.width * scale));
  const h = Math.max(1, Math.round(canvas.height * scale));
  const small = document.createElement("canvas");
  small.width = w;
  small.height = h;
  const ctx = small.getContext("2d");
  if (!ctx) return canvas; // can't scale: a heavy signature beats a lost one
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(canvas, 0, 0, w, h);
  return small;
}

export function exportSignature(pad, { trim = true } = {}) {
  if (!pad) return undefined;
  if (isPadRestoring(pad)) return undefined;
  const canvas = padCanvas(pad);
  if (!canvas) return undefined;
  if (!canvasHasInk(canvas)) return null;

  let out = canvas;
  if (trim && typeof pad.getTrimmedCanvas === "function") {
    try {
      const trimmed = pad.getTrimmedCanvas(); // copies, does not touch the pad
      if (canvasHasInk(trimmed)) out = trimmed;
    } catch { /* keep the full canvas */ }
  }
  try {
    out = downscaleCanvas(out);
  } catch { /* keep the full-size canvas */ }
  try {
    const url = out.toDataURL("image/png");
    return isBlankSignature(url) ? undefined : url;
  } catch {
    return undefined;
  }
}

// Width/height straight out of a PNG data URL's IHDR header, without decoding
// the image. Returns null for anything that isn't a parseable PNG.
function pngDimensions(dataURL) {
  const comma = dataURL.indexOf(",");
  if (comma < 0) return null;
  try {
    const head = atob(dataURL.slice(comma + 1, comma + 61)); // 60 b64 chars = 45 bytes
    if (head.length < 24 || head.charCodeAt(1) !== 0x50) return null; // "P" of PNG
    const at = (o) =>
      ((head.charCodeAt(o) << 24) | (head.charCodeAt(o + 1) << 16) |
       (head.charCodeAt(o + 2) << 8) | head.charCodeAt(o + 3)) >>> 0;
    return { width: at(16), height: at(20) };
  } catch {
    return null;
  }
}

// Is a STORED signature value actually blank? Drafts written before this fix
// carry 1x1 transparent PNGs (142 and 238 bytes) where a signature should be,
// and those must never be restored into a pad or drawn on a slip as if signed.
export function isBlankSignature(dataURL) {
  if (typeof dataURL !== "string" || !dataURL.startsWith("data:image/")) return true;
  const dims = pngDimensions(dataURL);
  if (dims) return dims.width <= 2 || dims.height <= 2;
  return dataURL.length < 512; // not a PNG we can read: real ink is never this small
}

const realSig = (s) => typeof s === "string" && !isBlankSignature(s);

// When a NEWER draft envelope displaces an older local copy, carry forward any
// signature the newer copy is missing (Hazelman 2026-08-20: queued pushes on
// ship wifi landed out of order, so the account briefly held an envelope
// missing the other pilot's ink; a device adopting it — timestamp-only compare
// — then lost the captain's and other pilot's signatures, the two kinds that
// exist NOWHERE but the envelope). Ink is only ever ADDED to the adopted copy,
// never replaced, and every other field keeps the newer envelope's value.
// Trade-off, accepted with the fix: a signature deliberately cleared on
// another device comes back if this device still holds it — clearing it again
// is one tap; recovering a lost one meant chasing the ship.
export function mergeSignaturesForward(localEnv, cloudEnv) {
  try {
    const loc = localEnv?.data;
    const cld = cloudEnv?.data;
    if (!loc || !cld) return cloudEnv;
    const sigsOf = (d) =>
      Array.isArray(d.pilot_signatures) ? d.pilot_signatures
        : Array.isArray(d.pilots) ? d.pilots.map((p) => p?.signature ?? null)
          : [];
    const locSigs = sigsOf(loc);
    const cldSigs = sigsOf(cld);
    const merged = [];
    let changed = false;
    for (let i = 0; i < Math.max(locSigs.length, cldSigs.length); i++) {
      if (!realSig(cldSigs[i]) && realSig(locSigs[i])) {
        merged[i] = locSigs[i];
        changed = true;
      } else {
        merged[i] = cldSigs[i] ?? null;
      }
    }
    const locMaster = realSig(loc.master_signature) ? loc.master_signature
      : realSig(loc.master?.signature) ? loc.master.signature : null;
    const cldHasMaster = realSig(cld.master_signature) || realSig(cld.master?.signature);
    const takeMaster = !cldHasMaster && locMaster;
    if (!changed && !takeMaster) return cloudEnv;
    const out = { ...cloudEnv, data: { ...cld } };
    if (changed) {
      out.data.pilot_signatures = merged;
      if (Array.isArray(out.data.pilots)) {
        out.data.pilots = out.data.pilots.map((p, i) =>
          p && !realSig(p.signature) && realSig(merged[i]) ? { ...p, signature: merged[i] } : p);
      }
    }
    if (takeMaster) out.data.master_signature = locMaster;
    return out;
  } catch {
    return cloudEnv; // never let the rescue path break an adopt
  }
}

// The expense-report twin of mergeSignaturesForward (Jill's own laptop,
// 2026-08-21: she signed the expense draft on her phone, the laptop adopted
// a newer unsigned account copy under mode=new, and the report went in
// unsigned). Expense drafts are flat report objects with one
// signature_base64 — same rule: ink only ever carries FORWARD into the
// adopted copy, never dropped because the newer copy lacks it. `from` is
// whichever copy LOST the newest-wins compare.
export function mergeExpenseSignatureForward(fromObj, intoObj) {
  try {
    if (!intoObj || realSig(intoObj.signature_base64) || !realSig(fromObj?.signature_base64)) return intoObj;
    return { ...intoObj, signature_base64: fromObj.signature_base64 };
  } catch {
    return intoObj; // never let the rescue path break an adopt
  }
}
